import {addMinutes, subMinutes} from 'date-fns'
import rafSchedule from 'raf-schd'
import React, {memo, useCallback, useEffect, useState} from 'react'
import styled from 'styled-components'
import {assembleCard} from '../../common/card-assembler/index'
import {GetAssetUrl} from '../../common/card-assembler/resolvers'
import {sanitizeThemePayload} from '../../common/themes'
import {
  AssembledCard,
  CardActionsV2,
  CardDefinition,
  EventDetail,
  Platform,
  UserProfileSnapshot
} from '../../common/types'
import {
  ScriptContext,
  VariablesRecord
} from '../../common/types/variable-engine'
import {ErrorBoundary} from '../../lib/error-boundary'
import {PreviewRendererChooser} from './preview-renderers'

const dummyScriptContext: ScriptContext = {
  cardInstance: {
    publishedAt: subMinutes(new Date(), 15).toISOString(),
    expires: addMinutes(new Date(), 25).toISOString(),
    firstSeen: subMinutes(new Date(), 5).toISOString(),
    priority: 5,
    isTest: true
  }
}

const VerticalWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const StyledPreview = styled.div<{cardRadius?: string}>`
  position: relative;

  letter-spacing: normal;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 10px;

  .card-content {
    min-height: 50px;
  }

  button,
  input,
  textarea {
    letter-spacing: normal;
  }

  // Stream wrapper
  > div {
    width: 420px;
    border-radius: ${p =>
      p.cardRadius
        ? `calc(${p.cardRadius} + 10px);`
        : `${p.theme.borderRadiusXL}`};
    border: 1px solid ${p => p.theme.grey05};
    overflow: hidden;
  }
`

interface SubviewControlProps {
  subviewId: string | null
  setSubviewId: (subviewId: string | null) => void
}

interface CardInstancePreviewProps extends SubviewControlProps {
  cardInstance: AssembledCard
  sdkVersion: string
  theme?: unknown
  setPreviewRef?: (el: HTMLElement | null) => void
  className?: string
  onClick?: React.MouseEventHandler<HTMLDivElement>
}

const CardInstancePreview = ({
  cardInstance,
  sdkVersion,
  subviewId,
  setSubviewId,
  theme: rawTheme,
  setPreviewRef,
  className,
  onClick
}: CardInstancePreviewProps) => {
  const theme = sanitizeThemePayload(rawTheme)

  // Always have the stream radius concentric to the card shape
  const cardRadius = theme.feed.shape.borderRadius.card

  return (
    <StyledPreview
      cardRadius={cardRadius}
      className={className}
      ref={ref => {
        if (setPreviewRef) {
          if (ref) {
            // the sdk preview is the first child of the wrapper ref
            const preview = ref.children[0] as HTMLElement
            // if we are showing the card list ( vs a subview )
            // then grab the card container, ignoring the container wrapper
            // otherwise if showing a subview we have no wrapper so can return it as the element
            const card = preview.querySelector<HTMLElement>('.card-container')
            setPreviewRef(card ?? preview)
          } else {
            setPreviewRef(ref)
          }
        }
      }}
      onClick={onClick}
    >
      <ErrorBoundary>
        <PreviewRendererChooser
          sdkVersion={sdkVersion}
          cardDefinition={cardInstance}
          currentSubviewId={subviewId}
          onNavigate={(path: string | null) => {
            setSubviewId(path)
          }}
          theme={theme}
        />
      </ErrorBoundary>
    </StyledPreview>
  )
}

const makeCardInstance = async ({
  cardDefinition,
  eventDetail,
  getAssetUrl,
  userProfile,
  environmentCardActions,
  variables
}: {
  cardDefinition: CardDefinition
  eventDetail: EventDetail
  getAssetUrl: GetAssetUrl
  userProfile?: UserProfileSnapshot
  environmentCardActions?: CardActionsV2
  variables: VariablesRecord
}) => {
  // use browser's timezone setting to assemble the preview
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  const card = await assembleCard(
    cardDefinition,
    eventDetail,
    variables,
    userProfile ?? {},
    {
      timezone
    },
    getAssetUrl,
    Platform.web,
    false,
    environmentCardActions,
    true,
    dummyScriptContext
  )
  return {
    instance: {
      id: 'myEvent|live|74439266-a5f0-49f3-a7ed-ca648d6c85c7|123',
      state: {},
      created: new Date()
    },
    ...card
  }
}

interface Props {
  [x: string]: any
}

const RafComponent = ({Component, ...props}: {Component: any}) => {
  const [state, setState] = useState<Props>(props)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const MemoComponent = useCallback(memo(Component), [])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateState = useCallback(
    rafSchedule((p: Props) => {
      setState(p)
    }),
    []
  )
  // cancel the raf scheduler, so it doesn't update the unmounted component
  useEffect(() => () => updateState.cancel(), [updateState])

  useEffect(() => {
    updateState(props)
  }, [updateState, props])

  return <MemoComponent {...state} />
}

const withRaf =
  <P extends {}>(Component: React.ComponentType<P>) =>
  (props: P) => <RafComponent Component={Component} {...props} />

interface CardPreviewProps extends SubviewControlProps {
  cardDefinition: CardDefinition
  eventDetail: EventDetail
  environmentCardActions?: CardActionsV2
  getAssetUrl: GetAssetUrl
  theme?: unknown
  setPreviewRef?: (el: HTMLElement | null) => void
  variables: VariablesRecord
  sdkVersion: string
  className?: string
  onClick?: React.MouseEventHandler<HTMLDivElement>
  profile?: Record<string, string>
}

export const CardPreview = withRaf(
  ({
    cardDefinition,
    eventDetail,
    environmentCardActions,
    getAssetUrl,
    setPreviewRef,
    variables,
    sdkVersion,
    profile,
    ...props
  }: CardPreviewProps) => {
    const [cardInstance, setCardInstance] = useState<AssembledCard>()

    useEffect(() => {
      makeCardInstance({
        cardDefinition,
        eventDetail,
        getAssetUrl,
        userProfile: {
          // use browser's timezone setting as a default
          // in an effort to make the preview as accurate as possible
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          ...profile
        },
        environmentCardActions,
        variables
      }).then(setCardInstance)
    }, [
      cardDefinition,
      eventDetail,
      profile,
      getAssetUrl,
      environmentCardActions,
      variables,
      sdkVersion // re-render when sdk version changes, fixes an issue where buttons dissapear
    ])

    return cardInstance ? (
      <VerticalWrap>
        <CardInstancePreview
          cardInstance={cardInstance}
          {...props}
          setPreviewRef={setPreviewRef}
          sdkVersion={sdkVersion}
        />
      </VerticalWrap>
    ) : null
  }
)
